import { H6, InputCurrency } from "@sellar-io/components";
import { generateContainerTypeDeliveryChargeRulesText } from "@sellar-io/common";
import {
  type FormAsyncValidateOrFn,
  type FormValidateOrFn,
  type ReactFormExtendedApi,
  useStore,
} from "@tanstack/react-form";
import {
  InputLabel,
  InputContainer,
  InputSelect,
  InputOption,
} from "@sellar-io/components";
import { formatPriceInputToApi } from "../../../../hooks/usePriceFormat";

export type ContainerTypeDeliveryChargeRulesFormType = {
  containerType: string;
  value: string;
  deliveryCharge: number;
};

type ContainerTypeDeliveryChargeRulesTanStackForm = ReactFormExtendedApi<
  ContainerTypeDeliveryChargeRulesFormType,
  undefined | FormValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormAsyncValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormAsyncValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormAsyncValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormAsyncValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  undefined | FormAsyncValidateOrFn<ContainerTypeDeliveryChargeRulesFormType>,
  unknown
>;

export const ContainerTypeDeliveryChargeRulesForm = ({
  form,
  formId,
}: {
  form: ContainerTypeDeliveryChargeRulesTanStackForm;
  formId?: string;
}) => {
  const { containerType, value } = useStore(form.store, (state) => ({
    containerType: state.values.containerType,
    value: state.values.value,
  }));
  const apiValue = formatPriceInputToApi(value);
  const deliveryRuleText = generateContainerTypeDeliveryChargeRulesText({
    containerType,
    value: apiValue ?? 0,
  });

  return (
    <form
      id={formId}
      onSubmit={(event) => {
        event.preventDefault();
        form.handleSubmit();
      }}
      className={"flex flex-col gap-2"}
    >
      <InputLabel>Set threshold</InputLabel>
      <form.Field name="value">
        {({ state, handleBlur, handleChange }) => (
          <InputContainer>
            <InputCurrency
              showPrefix
              allowNegative
              value={state.value}
              onBlur={handleBlur}
              onChange={(event) => {
                handleChange(event.target.value);
              }}
              required
            />
          </InputContainer>
        )}
      </form.Field>
      <H6 className="mb-1">
        How much your customer has to spend (ex. VAT) to get those items
        delivered free of charge.
      </H6>
      <InputLabel>Container type</InputLabel>
      <form.Field name="containerType">
        {({ state, handleBlur, handleChange }) => (
          <InputContainer className="mb-1">
            <InputSelect
              value={state.value}
              onBlur={handleBlur}
              onChange={(event) => {
                handleChange(event.target.value);
              }}
              required
            >
              <InputOption value="" disabled>
                Select a container type
              </InputOption>
              <InputOption value={"bottle"}>Bottle</InputOption>
              <InputOption value={"can"}>Can</InputOption>
              <InputOption value={"plasticKeg"}>Plastic keg</InputOption>
              <InputOption value={"steelKeg"}>Steel keg</InputOption>
              <InputOption value={"cask"}>Cask</InputOption>
              <InputOption value={"draught"}>
                Draught Item (Keg or cask)
              </InputOption>
              <InputOption value={"bagInBox"}>Bag In Box</InputOption>
              <InputOption value={"pouch"}>Pouch</InputOption>
              <InputOption value={"glass"}>Glass</InputOption>
              <InputOption value={"bag"}>Bag</InputOption>
              <InputOption value={"jar"}>Jar</InputOption>
            </InputSelect>
          </InputContainer>
        )}
      </form.Field>

      {!!containerType.length && apiValue !== null && (
        <div className={"rounded-lg bg-gray-100 p-4"}>{deliveryRuleText}</div>
      )}
    </form>
  );
};
